/**
 * AchievementSystem — listens on the bus, updates cumulative meta
 * counters, evaluates unmet achievement triggers, grants Genome Shards
 * on first-time satisfaction, and notifies the UI via 'achievement:earned'.
 *
 * Counters live under saveManager.getMeta().achievementProgress so they
 * survive death/prestige. Earned ids live under meta.achievements (order
 * = grant order). Shards live under meta.shards.
 *
 * Per-run state (death tracking, current stage start time) is held on
 * the system instance. Stages call `notifyStageStart` and forward the
 * stage:complete event so timing/no-death triggers can resolve.
 *
 * Triggers resolve forward only — existing players who already crossed a
 * threshold before this system shipped will earn the achievement on their
 * next eligible event. That's accepted as a one-time onboarding cost
 * rather than running a retroactive backfill at boot.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { player } from './PlayerState';
import type { AchievementDef, AchievementTrigger } from '@/data/types';

export class AchievementSystem {
  private defs: Record<string, AchievementDef>;
  private removers: (() => void)[] = [];

  /** Whether the player has died at least once in the current run.
   *  Reset by `notifyStageStart`. */
  private diedThisRun = false;
  /** Current stage id this system is tracking. Empty string if no run is active. */
  private currentStageId = '';

  constructor(defs: Record<string, AchievementDef>) {
    this.defs = defs;
    this.wire();
  }

  destroy(): void {
    for (const r of this.removers) r();
    this.removers = [];
  }

  /** Stage calls this on init/restart so per-run flags reset cleanly. */
  notifyStageStart(stageId: string): void {
    this.currentStageId = stageId;
    this.diedThisRun = false;
  }

  /** True if id is already earned. */
  isEarned(id: string): boolean {
    return saveManager.getMeta().achievements.includes(id);
  }

  /** Total earned count (used by the panel + cross.codex_X style triggers). */
  earnedCount(): number {
    return saveManager.getMeta().achievements.length;
  }

  /** Defs grouped by category, sorted by reward desc then title. */
  byCategory(): Record<string, AchievementDef[]> {
    const out: Record<string, AchievementDef[]> = {
      phase1: [], phase2: [], phase3: [], cross: [], mastery: [],
    };
    for (const def of Object.values(this.defs)) {
      (out[def.category] ??= []).push(def);
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => (b.reward - a.reward) || a.title.localeCompare(b.title));
    }
    return out;
  }

  // ------------------------------------------------------------------ wiring

  private wire(): void {
    const m = saveManager.getMeta();

    this.removers.push(bus.on('food:absorbed', ({ foodId }) => {
      const ap = m.achievementProgress;
      ap.foodAbsorbed[foodId] = (ap.foodAbsorbed[foodId] ?? 0) + 1;
      this.evaluateAll();
    }));

    this.removers.push(bus.on('predator:killed', ({ predatorId }) => {
      const ap = m.achievementProgress;
      ap.predatorsKilled += 1;
      ap.predatorsKilledById[predatorId] = (ap.predatorsKilledById[predatorId] ?? 0) + 1;
      this.evaluateAll();
    }));

    this.removers.push(bus.on('combo:streak', ({ count }) => {
      const ap = m.achievementProgress;
      if (count > ap.peakStreak) ap.peakStreak = count;
      this.evaluateAll();
    }));

    this.removers.push(bus.on('combo:changed', ({ combo }) => {
      const ap = m.achievementProgress;
      if (combo > ap.peakCombo) ap.peakCombo = combo;
      this.evaluateAll();
    }));

    this.removers.push(bus.on('biomass:changed', () => {
      this.evaluateAll();
    }));

    this.removers.push(bus.on('dna:changed', () => {
      this.evaluateAll();
    }));

    this.removers.push(bus.on('codex:unlocked', () => {
      this.evaluateAll();
    }));

    // --- Cambrian (stage 3) ---
    this.removers.push(bus.on('cambrian:kill', ({ byPlayer, victimTier }) => {
      if (!byPlayer) return;
      const eaten = (m.achievementProgress.cambrianEaten ??= {});
      eaten.all = (eaten.all ?? 0) + 1;
      eaten[victimTier] = (eaten[victimTier] ?? 0) + 1;
      this.evaluateAll();
    }));
    this.removers.push(bus.on('cambrian:evolved', ({ count }) => {
      const ap = m.achievementProgress;
      ap.peakEvolutions = Math.max(ap.peakEvolutions ?? 0, count);
      this.evaluateAll();
    }));
    this.removers.push(bus.on('cambrian:partGrown', ({ maxed }) => {
      const ap = m.achievementProgress;
      ap.peakPartsMaxed = Math.max(ap.peakPartsMaxed ?? 0, maxed);
      this.evaluateAll();
    }));
    this.removers.push(bus.on('cambrian:frenzy', ({ count }) => {
      const ap = m.achievementProgress;
      if (count > (ap.peakFrenzy ?? 0)) { ap.peakFrenzy = count; this.evaluateAll(); }
    }));
    this.removers.push(bus.on('cambrian:perfectLine', () => {
      const ap = m.achievementProgress;
      ap.perfectLines = (ap.perfectLines ?? 0) + 1;
      this.evaluateAll();
    }));
    this.removers.push(bus.on('cambrian:apex:killed', () => {
      const ap = m.achievementProgress;
      ap.apexKills = (ap.apexKills ?? 0) + 1;
      this.evaluateAll();
    }));

    this.removers.push(bus.on('player:died', () => {
      this.diedThisRun = true;
    }));

    this.removers.push(bus.on('stage:complete', ({ stageId, summary }) => {
      const ap = m.achievementProgress;
      if (!ap.stagesCleared.includes(stageId)) ap.stagesCleared.push(stageId);
      const elapsed = typeof summary.time === 'number' ? summary.time as number : 0;
      const prev = ap.bestStageTime[stageId];
      if (elapsed > 0 && (prev == null || elapsed < prev)) {
        ap.bestStageTime[stageId] = elapsed;
      }
      if (!this.diedThisRun && !ap.noDeathClears.includes(stageId)) {
        ap.noDeathClears.push(stageId);
      }
      this.evaluateAll();
    }));
  }

  // ------------------------------------------------------------------ evaluation

  private evaluateAll(): void {
    for (const def of Object.values(this.defs)) {
      if (this.isEarned(def.id)) continue;
      if (this.matches(def.trigger)) this.grant(def);
    }
  }

  private matches(t: AchievementTrigger): boolean {
    const meta = saveManager.getMeta();
    const ap = meta.achievementProgress;
    switch (t.kind) {
      case 'biomassReached': {
        // Current-run biomass — "reach N in a run" should not retro-grant
        // from a returning player's totalBiomassEver baseline. Scoped to
        // its stage so Phase 1 goals don't pop from Phase 2/3 biomass.
        if (t.stageId && t.stageId !== this.currentStageId) return false;
        return player.biomass >= t.amount;
      }
      case 'totalBiomassEver': return meta.totalBiomassEver >= t.amount;
      case 'dnaReached': {
        // Current DNA — "hold N at once". DNA resets to 0 on New Game and
        // resetAfterDeath, so this won't retro-grant on stage spawn.
        return player.dna >= t.amount;
      }
      case 'foodAbsorbed': {
        const n = ap.foodAbsorbed[t.foodId] ?? 0;
        return n >= t.count;
      }
      case 'predatorsKilled': {
        if (t.predatorId) return (ap.predatorsKilledById[t.predatorId] ?? 0) >= t.count;
        return ap.predatorsKilled >= t.count;
      }
      case 'streakCount': return ap.peakStreak >= t.minCount;
      case 'comboCount': return ap.peakCombo >= t.minCount;
      case 'stageCleared': {
        if (!ap.stagesCleared.includes(t.stageId)) return false;
        if (t.underSec != null) {
          const best = ap.bestStageTime[t.stageId];
          if (best == null || best > t.underSec) return false;
        }
        return true;
      }
      case 'noDeathClear': return ap.noDeathClears.includes(t.stageId);
      case 'codexUnlockedCount': return meta.codexUnlocked.length >= t.count;
      case 'creaturesEaten': return (ap.cambrianEaten?.[t.tier ?? 'all'] ?? 0) >= t.count;
      case 'evolutionsReached': return (ap.peakEvolutions ?? 0) >= t.count;
      case 'partsMaxed': return (ap.peakPartsMaxed ?? 0) >= t.count;
      case 'frenzyReached': return (ap.peakFrenzy ?? 0) >= t.minCount;
      case 'perfectLines': return (ap.perfectLines ?? 0) >= t.count;
      case 'apexSlain': return (ap.apexKills ?? 0) >= 1;
    }
  }

  private grant(def: AchievementDef): void {
    const meta = saveManager.getMeta();
    if (meta.achievements.includes(def.id)) return;
    meta.achievements.push(def.id);
    meta.shards += def.reward;
    saveManager.save();
    bus.emit('achievement:earned', {
      achievementId: def.id,
      reward: def.reward,
      title: def.title,
    });
    bus.emit('meta:shardsChanged', { shards: meta.shards });
    bus.emit('audio:cue', { id: 'codex', volume: 0.4 });
  }
}
